import { apiService, ApiResponse } from './api';
import {
  WebsiteAnalyticsDashboardData,
  WebsiteAnalyticsFilters,
  UserBehaviorMetrics,
  PageVisit,
  UserSession,
  ButtonClick,
  HeatmapData,
  ConversionFunnel,
  TrafficPattern
} from '../types/websiteAnalytics';

export class WebsiteAnalyticsService {
  // Use the NEW separate website-analytics endpoint
  private basePath = '/api/website-analytics';

  // Helper to build query string from filters
  private buildQueryString(filters?: WebsiteAnalyticsFilters): string {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof Date) {
            queryParams.append(key, value.toISOString());
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }
    return queryParams.toString();
  }

  // Helper to extract nested data from API response
  private extractData<T>(response: ApiResponse<any>): T | null {
    if (!response.success) return null;
    // API returns { success, data: { success, data: actualData } }
    // or { success, data: actualData }
    const outerData = response.data;
    if (outerData?.data !== undefined) {
      return outerData.data as T;
    }
    return outerData as T;
  }

  async getDashboardData(filters?: WebsiteAnalyticsFilters): Promise<ApiResponse<any>> {
    const queryString = this.buildQueryString(filters);
    const endpoint = queryString ? `${this.basePath}/dashboard?${queryString}` : `${this.basePath}/dashboard`;
    
    const response = await apiService.get<any>(endpoint);
    const data = this.extractData<any>(response);
    
    return {
      success: response.success && data !== null,
      data: data
    };
  }

  // Get available pages with click data for heatmap page selector
  async getAvailablePages(): Promise<ApiResponse<any[]>> {
    const response = await apiService.get<any>(`${this.basePath}/available-pages`);
    const data = this.extractData<any[]>(response);
    
    return {
      success: response.success && data !== null,
      data: data || []
    };
  }

  async getUserBehaviorMetrics(filters?: WebsiteAnalyticsFilters): Promise<ApiResponse<UserBehaviorMetrics>> {
    const queryString = this.buildQueryString(filters);
    const endpoint = queryString ? `${this.basePath}/user-behavior?${queryString}` : `${this.basePath}/user-behavior`;
    
    const response = await apiService.get<any>(endpoint);
    const rawData = this.extractData<any>(response);
    
    if (!rawData) {
      return { success: false, data: undefined };
    }

    // Transform API response to match UserBehaviorMetrics type
    const pageViews = rawData.pageViews || [];
    const buttonClicks = rawData.buttonClicks || [];
    const trafficPatterns = rawData.trafficPatterns || {};
    
    // Calculate totals from page views
    const totalPageViews = pageViews.reduce((sum: number, p: any) => sum + (p.views || 0), 0);
    const uniqueVisitors = pageViews.reduce((sum: number, p: any) => sum + (p.uniqueVisitors || 0), 0);
    
    // Transform device breakdown
    const deviceBreakdown = (trafficPatterns.deviceBreakdown || []).map((d: any) => ({
      deviceType: d.deviceType || 'unknown',
      sessions: d.count || 0,
      percentage: totalPageViews > 0 ? (d.count || 0) / totalPageViews : 0,
      averageDuration: 0,
      bounceRate: 0
    }));

    // Transform browser breakdown
    const browserBreakdown = (trafficPatterns.browserBreakdown || []).map((b: any) => ({
      browser: b.browser || 'Unknown',
      sessions: b.count || 0,
      percentage: totalPageViews > 0 ? (b.count || 0) / totalPageViews : 0
    }));

    // Transform top pages - now includes avgDuration and bounceRate from backend
    const topPages = pageViews.map((p: any) => ({
      page: p.page || '/',
      title: p.page || 'Unknown',
      views: p.views || 0,
      uniqueViews: p.uniqueVisitors || 0,
      averageDuration: p.avgDuration || 0,
      bounceRate: p.bounceRate || 0,
      exitRate: 0
    }));

    const metrics: UserBehaviorMetrics = {
      totalSessions: uniqueVisitors,
      totalPageViews,
      averageSessionDuration: 0,
      bounceRate: 0,
      pagesPerSession: uniqueVisitors > 0 ? totalPageViews / uniqueVisitors : 0,
      newUsers: uniqueVisitors,
      returningUsers: 0,
      topPages,
      topReferrers: [],
      deviceBreakdown,
      browserBreakdown,
      geographicData: []
    };

    return { success: true, data: metrics };
  }

  async getPageVisits(filters?: WebsiteAnalyticsFilters): Promise<ApiResponse<PageVisit[]>> {
    const queryString = this.buildQueryString(filters);
    const endpoint = queryString ? `${this.basePath}/page-visits?${queryString}` : `${this.basePath}/page-visits`;
    
    const response = await apiService.get<any>(endpoint);
    const data = this.extractData<PageVisit[]>(response);
    
    return {
      success: response.success && data !== null,
      data: data || []
    };
  }

  async getUserSessions(filters?: WebsiteAnalyticsFilters): Promise<ApiResponse<UserSession[]>> {
    const queryString = this.buildQueryString(filters);
    const endpoint = queryString ? `${this.basePath}/sessions?${queryString}` : `${this.basePath}/sessions`;
    
    const response = await apiService.get<any>(endpoint);
    const rawData = this.extractData<any[]>(response);
    
    if (!rawData || !Array.isArray(rawData)) {
      return { success: true, data: [] };
    }

    // Transform session data to match UserSession type
    const sessions: UserSession[] = rawData.map((s: any) => ({
      _id: s._id,
      sessionId: s.sessionId || '',
      userId: s.userId,
      startTime: new Date(s.startTime || Date.now()),
      endTime: s.endTime ? new Date(s.endTime) : undefined,
      duration: s.duration || 0,
      pageViews: s.pageViews || 0,
      bounced: s.bounced || false,
      deviceType: s.deviceType || 'desktop',
      browser: s.browser || 'Unknown',
      os: s.os || 'Unknown',
      country: s.country,
      city: s.city,
      referrer: s.referrer,
      landingPage: s.landingPage || '/',
      exitPage: s.exitPage,
      conversionEvents: s.conversionEvents || []
    }));

    return { success: true, data: sessions };
  }

  async getButtonClicks(filters?: WebsiteAnalyticsFilters): Promise<ApiResponse<any[]>> {
    const queryString = this.buildQueryString(filters);
    const endpoint = queryString ? `${this.basePath}/button-clicks?${queryString}` : `${this.basePath}/button-clicks`;
    
    const response = await apiService.get<any>(endpoint);
    const rawData = this.extractData<any[]>(response);
    
    if (!rawData || !Array.isArray(rawData)) {
      return { success: true, data: [] };
    }

    // Return raw data - the component expects { page, element, analyticsName, clicks, uniqueUsers }
    // Don't transform, just pass through the API response
    return { success: true, data: rawData };
  }

  async getHeatmapData(page: string, filters?: WebsiteAnalyticsFilters): Promise<ApiResponse<HeatmapData>> {
    const queryParams = new URLSearchParams({ page });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof Date) {
            queryParams.append(key, value.toISOString());
          } else {
            queryParams.append(key, String(value));
          }
        }
      });
    }
    
    const response = await apiService.get<any>(`${this.basePath}/heatmap?${queryParams.toString()}`);
    const rawData = this.extractData<any>(response);
    
    if (!rawData) {
      return {
        success: true,
        data: {
          page,
          clicks: [],
          scrollDepth: [],
          timeOnPage: 0,
          totalSessions: 0
        }
      };
    }

    // Transform heatmap data
    const heatmapData: HeatmapData = {
      page: rawData.page || page,
      clicks: (rawData.clicks || []).map((c: any) => ({
        x: c.x,
        y: c.y,
        count: c.count || c.clicks || 0,
        clicks: c.count || c.clicks || 0,
        element: c.element,
        uniqueUsers: c.uniqueUsers || 0
      })),
      scrollDepth: (rawData.scrollDepth || []).map((s: any) => ({
        depth: s.depth || 0,
        percentage: s.percentage || 0,
        sessions: s.sessions || 0
      })),
      timeOnPage: rawData.timeOnPage || 0,
      totalSessions: rawData.totalSessions || 0
    };

    return { success: true, data: heatmapData };
  }

  async getConversionFunnels(filters?: WebsiteAnalyticsFilters): Promise<ApiResponse<ConversionFunnel[]>> {
    const queryString = this.buildQueryString(filters);
    const endpoint = queryString ? `${this.basePath}/conversion-funnels?${queryString}` : `${this.basePath}/conversion-funnels`;
    
    const response = await apiService.get<any>(endpoint);
    const rawData = this.extractData<any>(response);
    
    // Handle both array and single object responses
    let funnelsArray: any[] = [];
    if (Array.isArray(rawData)) {
      funnelsArray = rawData;
    } else if (rawData && typeof rawData === 'object') {
      // Single funnel object from API
      funnelsArray = [rawData];
    }

    // Transform to ConversionFunnel type
    const funnels: ConversionFunnel[] = funnelsArray.map((f: any, index: number) => {
      const totalSessions = f.totalSessions || 0;
      const searchStarted = f.searchStarted || 0;
      const carSelected = f.carSelected || 0;
      const checkoutStarted = f.checkoutStarted || 0;
      const bookingCompleted = f.bookingCompleted || 0;

      // Build funnel steps from the data
      const steps = [
        {
          name: 'Visit Website',
          sessions: totalSessions,
          conversionRate: 1,
          dropOffRate: totalSessions > 0 ? (totalSessions - searchStarted) / totalSessions : 0
        },
        {
          name: 'Search Started',
          sessions: searchStarted,
          conversionRate: totalSessions > 0 ? searchStarted / totalSessions : 0,
          dropOffRate: searchStarted > 0 ? (searchStarted - carSelected) / searchStarted : 0
        },
        {
          name: 'Car Selected',
          sessions: carSelected,
          conversionRate: searchStarted > 0 ? carSelected / searchStarted : 0,
          dropOffRate: carSelected > 0 ? (carSelected - checkoutStarted) / carSelected : 0
        },
        {
          name: 'Checkout Started',
          sessions: checkoutStarted,
          conversionRate: carSelected > 0 ? checkoutStarted / carSelected : 0,
          dropOffRate: checkoutStarted > 0 ? (checkoutStarted - bookingCompleted) / checkoutStarted : 0
        },
        {
          name: 'Booking Completed',
          sessions: bookingCompleted,
          conversionRate: checkoutStarted > 0 ? bookingCompleted / checkoutStarted : 0,
          dropOffRate: 0
        }
      ];

      // Build drop-off points
      const dropOffPoints = [];
      for (let i = 0; i < steps.length - 1; i++) {
        if (steps[i].sessions > 0) {
          dropOffPoints.push({
            fromStep: steps[i].name,
            toStep: steps[i + 1].name,
            dropOffCount: steps[i].sessions - steps[i + 1].sessions,
            dropOffRate: steps[i].dropOffRate,
            commonExitPages: []
          });
        }
      }

      return {
        name: f.name || `Booking Funnel ${index + 1}`,
        steps,
        totalSessions,
        completionRate: totalSessions > 0 ? bookingCompleted / totalSessions : 0,
        dropOffPoints
      };
    });

    // If no funnels, create a default empty one
    if (funnels.length === 0) {
      funnels.push({
        name: 'Booking Funnel',
        steps: [
          { name: 'Visit Website', sessions: 0, conversionRate: 0, dropOffRate: 0 },
          { name: 'Search Started', sessions: 0, conversionRate: 0, dropOffRate: 0 },
          { name: 'Car Selected', sessions: 0, conversionRate: 0, dropOffRate: 0 },
          { name: 'Checkout Started', sessions: 0, conversionRate: 0, dropOffRate: 0 },
          { name: 'Booking Completed', sessions: 0, conversionRate: 0, dropOffRate: 0 }
        ],
        totalSessions: 0,
        completionRate: 0,
        dropOffPoints: []
      });
    }

    return { success: true, data: funnels };
  }

  async getTrafficPatterns(filters?: WebsiteAnalyticsFilters): Promise<ApiResponse<TrafficPattern>> {
    const queryString = this.buildQueryString(filters);
    const endpoint = queryString ? `${this.basePath}/traffic-patterns?${queryString}` : `${this.basePath}/traffic-patterns`;
    
    const response = await apiService.get<any>(endpoint);
    const rawData = this.extractData<any>(response);
    
    if (!rawData) {
      return {
        success: true,
        data: {
          hourlyTraffic: [],
          dailyTraffic: [],
          peakHours: [],
          seasonalTrends: []
        }
      };
    }

    // Transform hourly traffic
    const hourlyTraffic = (rawData.hourlyTraffic || []).map((h: any) => ({
      hour: h.hour || 0,
      sessions: h.sessions || 0,
      pageViews: h.pageViews || 0,
      averageDuration: h.averageDuration || 0
    }));

    // Transform device breakdown to peak hours (since API doesn't have peak hours)
    const peakHours = hourlyTraffic
      .filter((h: any) => h.sessions > 0)
      .map((h: any, index: number) => ({
        hour: h.hour,
        dayOfWeek: index % 7,
        averageSessions: h.sessions,
        peakSessions: h.sessions
      }))
      .sort((a: any, b: any) => b.peakSessions - a.peakSessions)
      .slice(0, 10);

    // Create daily traffic from hourly (aggregate)
    const dailyTraffic = [{
      date: new Date().toISOString(),
      sessions: hourlyTraffic.reduce((sum: number, h: any) => sum + h.sessions, 0),
      pageViews: hourlyTraffic.reduce((sum: number, h: any) => sum + h.pageViews, 0),
      uniqueUsers: hourlyTraffic.reduce((sum: number, h: any) => sum + h.sessions, 0),
      bounceRate: 0
    }];

    const trafficPattern: TrafficPattern = {
      hourlyTraffic,
      dailyTraffic,
      peakHours,
      seasonalTrends: []
    };

    return { success: true, data: trafficPattern };
  }

  async getRealTimeData(): Promise<ApiResponse<any>> {
    const response = await apiService.get<any>(`${this.basePath}/real-time`);
    const data = this.extractData<any>(response);
    return { success: response.success, data };
  }

  // Utility methods
  formatDuration(seconds: number): string {
    if (seconds < 60) {
      return `${Math.round(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return `${hours}h ${minutes}m`;
    }
  }

  // Format percentage - handles both decimal (0.28) and already-percentage (28) values
  formatPercentage(value: number): string {
    // If value is already > 1, it's already a percentage (e.g., 28 means 28%)
    // If value is <= 1, it's a decimal (e.g., 0.28 means 28%)
    if (value > 1) {
      return `${value.toFixed(1)}%`;
    }
    return `${(value * 100).toFixed(1)}%`;
  }

  formatNumber(value: number): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  }

  getDeviceIcon(deviceType: string): string {
    switch (deviceType) {
      case 'desktop': return '🖥️';
      case 'mobile': return '📱';
      case 'tablet': return '📱';
      default: return '💻';
    }
  }

  getBrowserIcon(browser: string): string {
    const browserLower = browser.toLowerCase();
    if (browserLower.includes('chrome')) return '🌐';
    if (browserLower.includes('firefox')) return '🦊';
    if (browserLower.includes('safari')) return '🧭';
    if (browserLower.includes('edge')) return '🌊';
    return '🌐';
  }

  calculateBounceRate(sessions: UserSession[]): number {
    if (sessions.length === 0) return 0;
    const bouncedSessions = sessions.filter(session => session.bounced).length;
    return bouncedSessions / sessions.length;
  }

  calculateAverageSessionDuration(sessions: UserSession[]): number {
    if (sessions.length === 0) return 0;
    const totalDuration = sessions.reduce((sum, session) => sum + (session.duration || 0), 0);
    return totalDuration / sessions.length;
  }

  getTopPages(pageVisits: PageVisit[], limit: number = 10) {
    const pageStats = new Map();
    
    pageVisits.forEach(visit => {
      const key = visit.page;
      if (!pageStats.has(key)) {
        pageStats.set(key, {
          page: visit.page,
          title: visit.title,
          views: 0,
          uniqueViews: new Set(),
          totalDuration: 0,
          exits: 0
        });
      }
      
      const stats = pageStats.get(key);
      stats.views++;
      stats.uniqueViews.add(visit.sessionId);
      if (visit.duration) {
        stats.totalDuration += visit.duration;
      }
      if (visit.exitPage) {
        stats.exits++;
      }
    });
    
    return Array.from(pageStats.values())
      .map(stats => ({
        page: stats.page,
        title: stats.title,
        views: stats.views,
        uniqueViews: stats.uniqueViews.size,
        averageDuration: stats.views > 0 ? stats.totalDuration / stats.views : 0,
        bounceRate: stats.views > 0 ? stats.exits / stats.views : 0,
        exitRate: stats.views > 0 ? stats.exits / stats.views : 0
      }))
      .sort((a, b) => b.views - a.views)
      .slice(0, limit);
  }
}

export const websiteAnalyticsService = new WebsiteAnalyticsService();